import React, {useMemo} from 'react';
import {HttpError} from 'api/http/types';
import type {DialogActionItem} from 'components/dialog/types';
import {useLocalization} from 'localization';
import {noOp} from 'services/utils';
import {Card, Layout, Spacer, useAlertDialog, useSnacks} from 'components';
import {DialogItems} from 'components/dialog/DialogItems';
import {DataFormV1} from 'schema/form';
import {useLinkedValueSearchContextProvider} from 'sceneExtensions';
import type {EditAgentProps} from '../types';
import {useAgentFormConfiguration} from '../hooks/useAgentFormConfiguration';
import {useEditAgentState} from '../hooks/useEditAgentState';

export const EditAgent: React.FC<EditAgentProps> = props => {
  const {t, tLoose} = useLocalization();
  const {errorSnack} = useSnacks();
  const LinkedValueSearchContext = useLinkedValueSearchContextProvider();
  const configuration = useAgentFormConfiguration();

  const {schema, initialEditAgent, hasChanges, showErrors, onChanged, save} =
    useEditAgentState(props);

  const confirmDeleteProps = useMemo(
    () => ({
      title: t('page.agents.delete.title'),
      body: t('page.agents.delete.body'),
      cancelTitle: t('general.cancel'),
      okTitle: t('general.ok'),
      onConfirm: () =>
        props.onDelete
          ? props.onDelete(initialEditAgent).catch(error => {
              if (error instanceof HttpError) {
                errorSnack(tLoose(`error.api.${error.message}`), error);
              } else {
                errorSnack(t('error.api.unknown'), error);
              }
            })
          : Promise.reject(),
      onCancel: () => undefined,
    }),
    [errorSnack, initialEditAgent, props, t, tLoose],
  );
  const {AlertDialog: ConfirmDeleteDialog, pleaseConfirm} =
    useAlertDialog(confirmDeleteProps);

  const actions = useMemo(() => {
    const deleteAction: DialogActionItem = {
      itemType: 'action',
      title: t('general.delete'),
      color: 'success',
      hide: !initialEditAgent.id,
      icon: 'Delete',
      showSpinnerOnClick: true,
      onClick: props.onDelete ? pleaseConfirm : noOp,
    };

    const cancelAction: DialogActionItem = {
      itemType: 'action',
      title: t('general.cancel'),
      variant: 'text',
      hide: !hasChanges,
      onClick: props.onCancel ?? noOp,
    };

    const saveAction: DialogActionItem = {
      itemType: 'action',
      title: t('general.save'),
      color: 'success',
      hide: !hasChanges,
      icon: 'Check',
      showSpinnerOnClick: true,
      onClick: save,
    };
    return [deleteAction, cancelAction, saveAction];
  }, [
    t,
    initialEditAgent.id,
    props.onDelete,
    props.onCancel,
    pleaseConfirm,
    hasChanges,
    save,
  ]);

  return (
    <Layout>
      <DialogItems dialogItems={actions} />
      <Spacer size={2} />
      <Card>
        <Card.Content>
          <LinkedValueSearchContext>
            <DataFormV1
              key={'edit-agent'}
              id={'edit-agent'}
              schema={schema}
              data={initialEditAgent}
              configuration={configuration}
              relatedData={{}}
              mode={'edit'}
              showErrors={showErrors}
              onDataChanged={onChanged}
            />
            <ConfirmDeleteDialog />
          </LinkedValueSearchContext>
        </Card.Content>
      </Card>
    </Layout>
  );
};
